// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type PropsWithChildren, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * DOM target accepted by {@link usePortal} and the {@link Portal} component.
 *
 * @remarks
 * Each value is resolved in {@link usePortal} after layout (`useLayoutEffect`):
 *
 * - **`string`**: `document.getElementById(value)`, or `null` when `document` is
 *   undefined (e.g. SSR).
 * - **`Element`**: that element.
 * - **`DocumentFragment`**: that fragment.
 * - **`Document`**: `document.body`.
 * - **`null`**: no mount; the hook returns `portal: null`.
 */
export type PortalContainer = string | Element | DocumentFragment | Document | null;

/** Arguments for {@link usePortal}. */
export interface UsePortalProps extends PropsWithChildren {
  /**
   * Where to mount {@link UsePortalProps.children} via `react-dom`
   * {@link createPortal}.
   */
  readonly container: PortalContainer;
}

/**
 * Maps `container` to the actual `Element` or `DocumentFragment` passed to
 * `createPortal`.
 *
 * @param container - Raw target from props.
 * @returns Node to portal into, or `null` if nothing should render.
 * @internal
 */
const resolveMount = (
  container: PortalContainer,
): Element | DocumentFragment | null => {
  switch (true) {
    case typeof container === 'string':
      return typeof document === 'undefined'
        ? null
        : document.getElementById(container);
    case container instanceof Element:
    case container instanceof DocumentFragment:
      return container;
    case container instanceof Document:
      return container.body;
    default:
      return null;
  }
};

/**
 * Hook that returns a React portal subtree for the given `container` and
 * `children`.
 *
 * @remarks
 * **Layout and state:** The resolved node is stored with `useState` and updated in
 * `useLayoutEffect` whenever `container` changes. That guarantees a re-render after
 * the DOM target is known (for example `getElementById` after the host element
 * exists, or an `Element` passed from state after a ref callback runs).
 *
 * **Cleanup:** On dependency change or unmount, the stored node is cleared so the
 * portal tears down before the next resolution.
 *
 * **SSR:** When `document` is unavailable, string ids resolve to `null` until
 * hydration.
 * @example Mount by stable id (`useId`)
 *
 * ```tsx
 * const mountId = useId();
 * const { portal } = usePortal({
 *   container: mountId,
 *   children: <Panel />,
 * });
 * return (
 *   <>
 *     <div id={mountId} />
 *     {portal}
 *   </>
 * );
 * ```
 *
 * @example Mount into an element from state (e.g. ref callback)
 *
 * ```tsx
 * const [host, setHost] = useState<HTMLDivElement | null>(null);
 * const { portal } = usePortal({
 *   container: host,
 *   children: <Toolbar />,
 * });
 * return (
 *   <>
 *     <div ref={setHost} />
 *     {portal}
 *   </>
 * );
 * ```
 *
 * @param props - `container` mount target and `children` to render inside the
 *   portal.
 * @returns Object with `portal`: a `ReactPortal`, or `null` until a mount node
 *   exists or when `container` resolves to `null`.
 */
export const usePortal = ({ container, children }: UsePortalProps) => {
  const [mountNode, setMountNode] = useState<Element | DocumentFragment | null>(
    null,
  );

  useLayoutEffect(() => {
    setMountNode(resolveMount(container));

    return () => {
      setMountNode(null);
    };
  }, [container]);

  const portal = mountNode !== null ? createPortal(children, mountNode) : null;

  return { portal } as const;
};
